import { authenticatedRequest } from 'utils/api';
import basicFlow, { genericErrorHandler } from './asyncHandler';
import * as actions from 'reducers/credenciais.reducer';
import { routeWatcher } from './rotas.saga';
import { put, select } from 'redux-saga/effects';
import { getPayload } from 'selectors/routes.selectors';
import { toast } from 'react-toastify';
import { normalizeCpfCnpj } from 'utils/basic';

import {
  actions as routeActions,
  types as routes
} from '../reducers/rotas.actions';

const apiListarCredenciais = () => {
  return authenticatedRequest({
    url: `/configuracao-credencial/`,
    method: 'get'
  });
};

const listarCredenciais = basicFlow({
  actionGenerator: actions.listarCredenciaisRequest,
  actionFailure: actions.listarCredenciaisFailure,
  actionSuccess: actions.listarCredenciaisSuccess,
  api: apiListarCredenciais,
  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error });
  }
});

const apiExcluirCredencial = ({ value }) => {
  return authenticatedRequest({
    url: `/configuracao-credencial/${value.id}`,
    method: 'delete'
  });
};

const excluirCredencial = basicFlow({
  actionGenerator: actions.excluirCredencialRequest,
  actionFailure: actions.excluirCredencialFailure,
  actionSuccess: actions.excluirCredencialSuccess,
  api: apiExcluirCredencial,
  postSuccess: function* () {
    yield toast.success('Credencial excluída com sucesso.');
    yield put(actions.listarCredenciaisRequest());
  },
  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error });
  }
});

const formatDateToISO = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('/');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

const buildCredencialFormData = ({ value, privateKeyFile, certificateFile }) => {
  const formData = new FormData();
  formData.append('descricao', value.descricao);
  formData.append('tipo', value.tipo);
  formData.append('default', value.default ? '1' : '0');
  formData.append('dataExpiracao', formatDateToISO(value.dataExpiracao));
  formData.append('clientId', value.clientId ?? '');
  formData.append('clientSecret', value.clientSecret ?? '');
  formData.append('apiId', value.apiId ?? '');
  formData.append('login', value.login ?? '');
  formData.append('cpfCnpj', normalizeCpfCnpj(value.cpfCnpj ?? ''));
  if (privateKeyFile) formData.append('privateKey', privateKeyFile, privateKeyFile.name);
  if (certificateFile) formData.append('certificate', certificateFile, certificateFile.name);
  return formData;
};

const apiCriarCredencial = (payload) => {
  return authenticatedRequest({
    url: `/configuracao-credencial/`,
    method: 'post',
    body: buildCredencialFormData(payload),
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

const criarCredencial = basicFlow({
  actionGenerator: actions.criarCredencialRequest,
  actionFailure: actions.criarCredencialFailure,
  actionSuccess: actions.criarCredencialSuccess,
  api: apiCriarCredencial,
  postSuccess: function* () {
    toast.success('Credencial incluída com sucesso.');
    yield put(routeActions.redirectTo(routes.CREDENCIAIS));
  },
  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error });
  }
});

const apiMostrarCredencial = (id) => {
  return authenticatedRequest({
    url: `/configuracao-credencial/${id}`,
    method: 'get'
  });
};

const mostrarCredencial = basicFlow({
  actionGenerator: actions.mostrarCredencialRequest,
  actionFailure: actions.mostrarCredencialFailure,
  actionSuccess: actions.mostrarCredencialSuccess,
  api: apiMostrarCredencial,
  postFailure: function* () {
    yield toast.error('Credencial não encontrada.');
  }
});

const apiEditarCredencial = (payload) => {
  return authenticatedRequest({
    url: `/configuracao-credencial/${payload.value.id}`,
    method: 'put',
    body: buildCredencialFormData(payload),
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

const editarCredencial = basicFlow({
  actionGenerator: actions.editarCredencialRequest,
  actionFailure: actions.editarCredencialFailure,
  actionSuccess: actions.editarCredencialSuccess,
  api: apiEditarCredencial,
  postSuccess: function* () {
    toast.success('Credencial editada com sucesso.');
    const { idCredencial } = yield select(getPayload);
    yield put(actions.mostrarCredencialRequest(idCredencial));
  },
  postFailure: function* ({ error }) {
    yield genericErrorHandler({ error });
  }
});

function* listarCredenciaisRouteWatcher() {
  yield routeWatcher(routes.CREDENCIAIS, function* () {
    yield put(actions.listarCredenciaisRequest());
  });
}

function* mostrarCredencialRouteWatcher() {
  yield routeWatcher(routes.CREDENCIAL, function* () {
    const { idCredencial } = yield select(getPayload);
    if (idCredencial === 'novo') {
      yield put(actions.desativarModoEdicao());
      yield put(actions.limparCredencial());
      return;
    }
    if (!idCredencial) return;
    yield put(actions.mostrarCredencialRequest(idCredencial));
    yield put(actions.ativarModoEdicao());
  });
}

export const sagas = [
  listarCredenciais.watcher(),
  excluirCredencial.watcher(),
  criarCredencial.watcher(),
  mostrarCredencial.watcher(),
  editarCredencial.watcher(),
  listarCredenciaisRouteWatcher(),
  mostrarCredencialRouteWatcher()
];
